import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, ShieldCheck } from "lucide-react";
import VisionLogo from "@/components/VisionLogo";
import { useAuth } from "@/context/AuthContext";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [showPwd, setShowPwd] = useState(false);
  const [username, setUsername] = useState("");
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!username.trim() || !pwd) {
      setError("Please enter your username and password.");
      return;
    }
    setBusy(true);
    const res = await login(username.trim(), pwd);
    setBusy(false);
    if (res.ok) {
      const from = location.state?.from;
      navigate(from && from !== "/login" ? from : "/dashboard", { replace: true });
    } else {
      setError(res.error || "Login failed.");
    }
  };

  return (
    <div
      className="relative min-h-screen w-full bg-vision-navy-gradient text-white grain-overlay"
      data-testid="login-page"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(closest-side, #F79009 0%, transparent 70%)" }}
      />

      <div className="mx-auto flex min-h-screen max-w-[1400px] flex-col px-6 pb-10 pt-8">
        <div className="flex items-center vision-rise">
          <VisionLogo size={26} tone="white" />
        </div>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[460px] text-center">
            <div className="mb-10 flex justify-center vision-rise">
              <VisionLogo size={68} tone="white" />
            </div>

            <h1
              className="vision-rise vision-rise-d1 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-[56px]"
              data-testid="login-headline"
            >
              Command every shift,<br />
              every site,<br />
              in one place.
            </h1>

            <p
              className="vision-rise vision-rise-d2 mx-auto mt-5 max-w-[380px] text-sm leading-relaxed text-white/75 sm:text-base"
              data-testid="login-subhead"
            >
              Vision workforce console for Facilities &amp; Security operations. Sign in with your issued username.
            </p>

            <form
              onSubmit={handleSubmit}
              className="vision-rise vision-rise-d3 mx-auto mt-9 flex w-full max-w-[380px] flex-col gap-3 text-left"
              data-testid="login-form"
              noValidate
            >
              <label className="text-[12px] font-semibold uppercase tracking-[0.14em] text-white/70">
                Username
              </label>
              <input
                data-testid="login-username"
                type="text"
                autoComplete="username"
                placeholder="your-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-full border border-white/20 bg-white/10 px-5 py-3.5 text-sm text-white placeholder:text-white/45 outline-none transition focus:border-white/70 focus:bg-white/15"
              />

              <label className="mt-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-white/70">
                Password
              </label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="w-full rounded-full border border-white/20 bg-white/10 px-5 py-3.5 pr-12 text-sm text-white placeholder:text-white/45 outline-none transition focus:border-white/70 focus:bg-white/15"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                  data-testid="login-toggle-password"
                  aria-label="Toggle password visibility"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="mt-1 rounded-2xl border px-4 py-3 text-left text-sm"
                  style={{
                    backgroundColor: "rgba(231, 76, 60, 0.14)",
                    borderColor: "rgba(231, 76, 60, 0.5)",
                    color: "#FECACA",
                  }}
                >
                  {error}
                </div>
              )}

              <div className="mt-4 flex flex-col gap-3">
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit"
                  className="btn-pill inline-flex items-center justify-center gap-2 bg-white px-6 py-3.5 text-sm font-semibold text-[color:var(--vision-navy)] shadow-card transition hover:bg-white/95 hover:shadow-card-lg disabled:opacity-70"
                >
                  {busy ? "Signing in…" : "Sign in"}
                  {!busy && <ArrowRight className="h-4 w-4" />}
                </button>

                <button
                  type="button"
                  data-testid="login-sso"
                  disabled
                  title="SSO — coming soon"
                  className="btn-pill inline-flex cursor-not-allowed items-center justify-center gap-2 border border-white/30 px-6 py-3.5 text-sm font-semibold text-white/60"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Continue with SSO
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="vision-rise vision-rise-d4 mt-auto flex flex-col items-center justify-between gap-2 text-[11px] text-white/50 md:flex-row">
          <div>© 2026 Vision — Integrated Manpower Solutions</div>
          <div className="flex items-center gap-4">
            <span>Privacy</span>
            <span>Terms</span>
            <span>Status</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
